/**
 * TableSkeleton
 *
 * Loading skeleton component that matches the exact structure of DailyBriefTable.
 * Displays animated shimmer effect while data is being fetched.
 *
 * @component
 * @param {number} rows - Number of skeleton rows to display (default: 5)
 */
import React from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/core';
import PropTypes from 'prop-types';

const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

// Shared shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
`;

// Shared shimmer styles
const shimmerStyles = css`
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e8e8e8 50%,
    #f0f0f0 75%
  );
  background-size: 400px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const TableRow = styled('tr')`
  box-sizing: border-box;
  width: 100%;
  height: 56px;
  background: #ffffff;
  border-bottom: 1px solid #eeeeee;

  &:hover {
    background-color: #fafafa;
  }
  
  &:last-child td {
    border-bottom: none;
  }
`;

const TableDataCell = styled('td')`
  box-sizing: border-box;
  padding: 8px 10px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  line-height: 150%;
  color: #444444;
  vertical-align: middle;
  background: #ffffff;
  height: 56px;
  border-left: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;

  &:first-of-type {
    min-width: 200px;
    width: 200px;
    border-left: none;
  }

  &:nth-of-type(2) {
    min-width: 386px;
    width: 35%;
    padding: 16px;
    gap: 4px;
  }

  &:nth-of-type(3) {
    width: auto;
    padding: 8px;
  }

  &:nth-of-type(4) {
    min-width: 240px;
    width: auto;
    padding: 16px;
    text-align: center;
  }
`;

const SkeletonBox = styled('div')`
  height: ${(props) => props.height || '20px'};
  width: ${(props) => props.width || '100%'};
  ${shimmerStyles}
  border-radius: 4px;
`;

const DaysLeftSkeleton = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const AthleteCell = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 16px;
`;

const Avatar = styled('div')`
  width: 40px;
  height: 40px;
  ${shimmerStyles}
  border-radius: 100px;
  box-shadow: 0px 2px 4px rgba(48, 49, 51, 0.1),
    0px 0px 1px rgba(48, 49, 51, 0.05);
  flex-shrink: 0;
`;

const AthleteName = styled('div')`
  height: 20px;
  flex-grow: 1;
  ${shimmerStyles}
  border-radius: 4px;
`;

const CompletionContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 4px;
  width: 100%;
`;

const WeekBoxSkeleton = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0;
  gap: 8px;
  height: 24px;
  flex: 1;
  width: 78px;
  ${shimmerStyles}
  border-radius: 4px;
`;

const AlertsContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  width: 100%;
`;

const DayColumn = styled('div')`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  align-content: flex-start;
  padding: 8px;
  gap: 4px;
  flex: 1;
  min-height: 40px;
  border-left: 1px solid #eeeeee;
  
  &:first-of-type {
    border-left: none;
  }
`;

const AlertSkeleton = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 4px;
  gap: 8px;
  height: 24px;
  width: 45px;
  ${shimmerStyles}
  border-radius: 4px;
`;

const TableSkeleton = ({ rows = 5 }) => {
  const skeletonVariations = [
    { yesterday: 0, today: 0 },
    { yesterday: 1, today: 1 },
    { yesterday: 2, today: 3 },
    { yesterday: 1, today: 2 },
    { yesterday: 0, today: 1 },
  ];

  return (
    <>
      {Array.from({ length: rows }).map((_, index) => {
        const variation = skeletonVariations[index % skeletonVariations.length];
        return (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`skeleton-${index}`}>
            {/* Name column - matches AthleteCell structure exactly */}
            <TableDataCell>
              <AthleteCell>
                <Avatar />
                <AthleteName />
              </AthleteCell>
            </TableDataCell>

            {/* Workout completion column - matches CompletionWeeks structure exactly */}
            <TableDataCell>
              <CompletionContainer>
                <WeekBoxSkeleton />
                <WeekBoxSkeleton />
                <WeekBoxSkeleton />
                <WeekBoxSkeleton />
              </CompletionContainer>
            </TableDataCell>

            {/* Days Left column - matches DaysLeftValue structure exactly */}
            <TableDataCell>
              <DaysLeftSkeleton>
                <SkeletonBox height='24px' width='60px' />
              </DaysLeftSkeleton>
            </TableDataCell>

            {/* Questionnaire alerts column - matches AlertsColumn structure */}
            <TableDataCell>
              <AlertsContainer>
                <DayColumn>
                  {SKELETON_KEYS.slice(0, variation.yesterday).map((key) => (
                    <AlertSkeleton key={`yesterday-${key}`} />
                  ))}
                </DayColumn>
                <DayColumn>
                  {SKELETON_KEYS.slice(0, variation.today).map((key) => (
                    <AlertSkeleton key={`today-${key}`} />
                  ))}
                </DayColumn>
              </AlertsContainer>
            </TableDataCell>
          </TableRow>
        );
      })}
    </>
  );
};

TableSkeleton.propTypes = {
  rows: PropTypes.number,
};

TableSkeleton.defaultProps = {
  rows: 5,
};

export default TableSkeleton;
